import classnames from 'classnames/bind'
import { useRouter } from 'next/router'
import { Fragment, useRef } from 'react'
import { useTranslate } from 'react-polyglot'
import { GlobalGridPreset } from '~/@types/grid-preset'

import { useMeasureObserver, useWindowSize } from '@unlikelystudio/react-hooks'
import Slider, { useSliderState } from '@unlikelystudio/react-slider'

import { ArrowLeftIcon, ArrowRightIcon } from '~/components/Abstracts/Icons'
import VideoComponentForwarded from '~/components/Abstracts/Video'
import ProductHeaderEditoImage from '~/components/Slices/ProductHeaderEditoImage'
import Image from '~/components/UI/Image'
import ProductImagesZoom from '~/components/UI/ProductHeader/ProductImagesZoom'

import { useProductZoomContext } from '~/providers/ProductZoomProvider'
import { useShopifyProductContext } from '~/providers/ShopifyProductProvider'
import { useStyle } from '~/providers/StyleProvider'

import useFallbackVariants from '~/hooks/useFallbackVariants'
import useVariant from '~/hooks/useVariant'

import deepEqualCompareVariants from '~/utils/deep-equal-compare-variants'
import getCurrentImagePDP from '~/utils/get-current-image-pdp'
import {
  getIsProductFashion,
  getIsProductFragrance,
} from '~/utils/get-shopify-product-type'

import { GENERAL } from '~/data/dictionary'

import ProductDetails from './ProductDetails'
import css from './styles.module.scss'
import { ProductHeaderProps } from './types'

const cx = classnames.bind(css)

const IMAGE_SIZES = [{ breakpoint: 'lg', ratio: 16 / 24 }, { ratio: 22 / 24 }]

function ProductHeader({
  className,
  images,
  medias,
  type,
  ...rest
}: ProductHeaderProps) {
  const t = useTranslate()
  const { query } = useRouter()
  const hasQueryVariant = Boolean(query?.variant)
  const customSliderRef = useRef()

  const [
    { nextSlide, prevSlide, slideIndex, enableDrag, maxSlideIndex },
    setSliderState,
  ] = useSliderState()

  const {
    images: zoomImages,
    isZoom,
    hasZoom,
    openZoom,
    closeZoom,
    incrementIndex,
    decrementIndex,
    resetIndex,
    imageIndex,
  } = useProductZoomContext()

  const gridStyle = useStyle({ grid: GlobalGridPreset.BASE_GRID })
  const productDetailRef = useRef()
  const productDetailBounds = useMeasureObserver(
    productDetailRef,
    'getBoundingClientRect',
  )
  const windowSize = useWindowSize()
  // Sticky on both direction product details
  const style = {
    top:
      windowSize.height < productDetailBounds.height
        ? windowSize.height - productDetailBounds.height
        : 0,
  }

  // Getting the product context, with the real-time info from Shopify
  const {
    variants: updatedVariants,
    defaultVariant: updatedDefaultVariant,
    isLoading,
    isSuccess,
  } = useShopifyProductContext()

  // Getting the useQuery state
  const isFullyDoneLoadingUpdatedVariants = !isLoading && isSuccess

  // Getting the equality of the set of variants
  const isVariantsEqual = deepEqualCompareVariants(
    updatedVariants,
    rest?.variants,
  )

  // Getting the correct variant values and the correct props depending on the variants from the server and the ones from Shopify
  const { fallbackVariantValues, compiledVariantProps } = useFallbackVariants({
    baseVariants: rest?.variants,
    baseDefaultVariant: rest?.defaultVariant,
    updatedVariants,
    updatedDefaultVariant,
    isUpdatedVariantsLoaded: isFullyDoneLoadingUpdatedVariants,
    isVariantsEqual,
  })

  // Processed variant from routing param
  const { selectedVariant, variantIndex } = useVariant(
    fallbackVariantValues?.variants,
    fallbackVariantValues?.defaultVariant,
  )

  // If product type = Sets or product type = Fashion then display the first image of the array
  // else if Fashion display index of variant inside images array
  const currentImage = getCurrentImagePDP(
    type,
    medias,
    images,
    variantIndex,
    hasQueryVariant,
  )

  return (
    <section
      className={cx(className, gridStyle, css.ProductHeader, { isZoom })}>
      <div className={css.productHeaderContent}>
        {/* [FRAGRANCE - SETS] - First video or image  */}
        {!getIsProductFashion(type) && (
          <>
            {currentImage?.image && (
              <Image
                className={cx(css.mainImage, {
                  hasZoom: hasZoom && getIsProductFashion(type),
                })}
                layout="fill"
                objectFit="cover"
                ratio={cx(css.ratio, { isFashion: getIsProductFashion(type) })}
                sizesFromBreakpoints={IMAGE_SIZES}
                asPlaceholder
                priority={true}
                {...currentImage.image}
                alt={`${rest?.name ?? ''} ${selectedVariant?.title ?? ''}`}
                onClick={() => {
                  if (getIsProductFashion(type)) {
                    openZoom()
                  }
                }}
              />
            )}

            {currentImage?.video && (
              <VideoComponentForwarded
                className={css.mainVideo}
                poster={currentImage.video.poster}
                sources={currentImage.video.sources}
                autoPlay
                muted
                loop
                playsInline
              />
            )}
          </>
        )}

        {/* [FASHION] - Mobile slider */}
        {getIsProductFashion(type) && medias?.length > 0 && (
          <div
            className={cx(
              css.fashionMobileSliderContainer,
              css.hideOnLargeScreen,
            )}>
            {medias.length > 1 && (
              <>
                <button
                  className={cx(css.mobileSliderArrows, css.prev, {
                    [css.disabled]: slideIndex === 0,
                  })}
                  onClick={prevSlide}
                  aria-label={t(GENERAL.SLIDER_BUTTON_LEFT)}>
                  <ArrowLeftIcon className={cx(css.icon)} />
                </button>
                <button
                  className={cx(css.mobileSliderArrows, css.next, {
                    [css.disabled]: slideIndex === maxSlideIndex,
                  })}
                  onClick={nextSlide}
                  aria-label={t(GENERAL.SLIDER_BUTTON_RIGHT)}>
                  <ArrowRightIcon className={cx(css.icon)} />
                </button>
              </>
            )}
            <Slider
              className={cx(css.slider)}
              setSliderState={setSliderState}
              customSliderRef={customSliderRef}
              autoBlockSlideIndexChange={true}
              snap>
              {medias.map(({ image, video }, index) => {
                if (video) {
                  return (
                    <div
                      key={`mobile-slider-video-${index}`}
                      className={cx(css.fashionMobileSliderItem)}>
                      <VideoComponentForwarded
                        poster={video.poster}
                        sources={video.sources}
                        autoPlay
                        muted
                        loop
                        playsInline
                      />
                    </div>
                  )
                }

                if (image) {
                  return (
                    <ProductHeaderEditoImage
                      key={`mobile-slider-image-${index}`}
                      className={cx(css.fashionMobileSliderItem)}
                      image={image}
                    />
                  )
                }

                return null
              })}
            </Slider>
          </div>
        )}

        <ProductDetails
          className={cx(css.productDetails, css.hideOnLargeScreen)}
          selectedVariant={selectedVariant}
          images={images}
          type={type}
          {...rest}
          {...compiledVariantProps}
        />

        {/*[FASHION] - Images rest only on desktop */}
        {getIsProductFashion(type) && medias?.length > 0 && (
          <div className={cx(css.headerSlices, css.hideOnSmallScreen)}>
            {medias?.map(({ image, video }, index) => {
              return (
                <Fragment key={index}>
                  {image && (
                    <ProductHeaderEditoImage
                      className={css.image}
                      image={image}
                    />
                  )}
                  {video && (
                    <VideoComponentForwarded
                      className={css.video}
                      poster={video.poster}
                      sources={video.sources}
                      autoPlay
                      muted
                      loop
                      playsInline
                    />
                  )}
                </Fragment>
              )
            })}
          </div>
        )}
      </div>

      <div
        ref={productDetailRef}
        className={cx(css.productDetails, css.hideOnSmallScreen)}
        style={style}>
        <ProductDetails
          selectedVariant={selectedVariant}
          triggerOnLoadEvent
          images={images}
          type={type}
          {...rest}
          {...compiledVariantProps}
        />
      </div>

      {medias?.length > 0 && (
        <ProductImagesZoom
          className={cx('productImagesZoom')}
          isFragrance={getIsProductFragrance(type)}
          isZoomed={isZoom}
          images={zoomImages}
          imageIndex={imageIndex}
          openZoom={() => {
            openZoom()
          }}
          closeZoom={() => {
            closeZoom()
            resetIndex()
          }}
          onNextClick={() => {
            decrementIndex(zoomImages)
          }}
          onPreviousClick={() => {
            incrementIndex(zoomImages)
          }}
        />
      )}
    </section>
  )
}

ProductHeader.defaultProps = {}

export default ProductHeader
